import React from "react";
import { PrimaryBlueButton, Severity } from "@velaro/velaro-component-lib";
import { Toggle } from "@/components/Toggle";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { SiteSettings } from "../types";

interface SupportAccessSettingsProps {
  siteSettings: SiteSettings;
  setSiteSettings: (updater: React.SetStateAction<SiteSettings>) => void;
  isVisible: (text: string) => boolean;
}

export default function SupportAccessSettings({
  siteSettings,
  setSiteSettings,
  isVisible
}: SupportAccessSettingsProps) {
  const api = useApi();
  const { displayToast } = useToastAlert();
  const [saving, setSaving] = React.useState(false);

  // allowSupportAccess = true when DisableSupportUsers is false (the default)
  const allowSupportAccess = !(siteSettings.disableSupportUsers ?? false);

  async function handleToggle() {
    const newDisableValue = !siteSettings.disableSupportUsers;
    setSiteSettings((prev) => ({ ...prev, disableSupportUsers: newDisableValue }));

    setSaving(true);
    try {
      const response = await api.messaging.post("SiteSettings", {
        ...siteSettings,
        disableSupportUsers: newDisableValue
      });
      if (response.ok) {
        displayToast(Severity.Success, "Support access settings saved");
      } else {
        // Revert on failure
        setSiteSettings((prev) => ({ ...prev, disableSupportUsers: !newDisableValue }));
        displayToast(Severity.Error, "Failed to save support access settings");
      }
    } catch {
      // Revert on error
      setSiteSettings((prev) => ({ ...prev, disableSupportUsers: !newDisableValue }));
      displayToast(Severity.Error, "Failed to save support access settings");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-2xl">
      {isVisible("support access") && (
        <div className="mb-8 p-5 border border-slate-200 rounded-lg bg-white">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-sm font-medium text-slate-800">Velaro Support Access</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Allow Velaro support staff to access this account
              </p>
            </div>
            <Toggle
              toggled={allowSupportAccess}
              changeToggle={handleToggle}
            />
          </div>

          <p className="text-xs text-slate-400 mt-3">
            When enabled, Velaro support staff can create a temporary user to diagnose issues in
            your account. Support users are automatically removed within 2 hours. Disable this only
            if your organization's security policy requires it.
          </p>

          {!allowSupportAccess && (
            <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
              Support access is disabled. Velaro staff will not be able to create temporary users for
              troubleshooting. This may slow response times for support requests.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
